import React, { useEffect, useState } from 'react';
import { Download, X } from 'lucide-react';

/**
 * Floating "Install App" prompt for NightBuZz PWA.
 * - Appears when the browser fires `beforeinstallprompt` (Android Chrome / desktop Chromium).
 * - On iOS Safari (which doesn't fire that event), shows a one-time hint to use Share -> Add to Home Screen.
 * - Hides itself once installed or dismissed (remembers dismissal for 7 days via localStorage).
 */
const DISMISS_KEY = 'nightbuzz_pwa_install_dismissed_until';
const DISMISS_DAYS = 7;

const isIos = () => {
  if (typeof navigator === 'undefined') return false;
  return /iphone|ipad|ipod/i.test(navigator.userAgent);
};

const isStandalone = () => {
  if (typeof window === 'undefined') return false;
  return (
    window.matchMedia?.('(display-mode: standalone)').matches ||
    window.navigator.standalone === true
  );
};

const isDismissed = () => {
  try {
    const until = parseInt(localStorage.getItem(DISMISS_KEY) || '0', 10);
    return until > Date.now();
  } catch {
    return false;
  }
};

const dismissFor = (days) => {
  try {
    localStorage.setItem(DISMISS_KEY, String(Date.now() + days * 24 * 60 * 60 * 1000));
  } catch {
    /* ignore */
  }
};

export default function PWAInstallPrompt() {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [visible, setVisible] = useState(false);
  const [iosHint, setIosHint] = useState(false);

  useEffect(() => {
    if (isStandalone() || isDismissed()) return;

    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setVisible(true);
    };

    window.addEventListener('beforeinstallprompt', handler);

    const installedHandler = () => {
      setVisible(false);
      setIosHint(false);
      setDeferredPrompt(null);
    };
    window.addEventListener('appinstalled', installedHandler);

    // iOS Safari fallback: no beforeinstallprompt available
    if (isIos() && !isStandalone()) {
      const t = setTimeout(() => setIosHint(true), 4000);
      return () => {
        clearTimeout(t);
        window.removeEventListener('beforeinstallprompt', handler);
        window.removeEventListener('appinstalled', installedHandler);
      };
    }

    return () => {
      window.removeEventListener('beforeinstallprompt', handler);
      window.removeEventListener('appinstalled', installedHandler);
    };
  }, []);

  const handleInstall = async () => {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    try {
      await deferredPrompt.userChoice;
    } catch {
      /* ignore */
    }
    setDeferredPrompt(null);
    setVisible(false);
  };

  const handleClose = () => {
    setVisible(false);
    setIosHint(false);
    dismissFor(DISMISS_DAYS);
  };

  if (!visible && !iosHint) return null;

  return (
    <div
      data-testid="pwa-install-prompt"
      className="fixed bottom-4 left-4 right-4 z-[9998] mx-auto max-w-md rounded-2xl border border-purple-500/40 bg-gradient-to-br from-zinc-900 via-zinc-900 to-purple-950/40 px-4 py-3 shadow-2xl backdrop-blur-md sm:bottom-6"
      role="dialog"
      aria-label="Install NightBuZz"
    >
      <div className="flex items-start gap-3">
        <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl bg-purple-600/20 ring-1 ring-purple-500/40">
          <Download className="h-5 w-5 text-purple-300" />
        </div>
        <div className="flex-1">
          <p className="text-sm font-semibold text-white">Install NightBuZz</p>
          {visible && deferredPrompt ? (
            <p className="mt-0.5 text-xs text-zinc-300">
              Get the full-screen app experience — no app store required.
            </p>
          ) : (
            <p className="mt-0.5 text-xs text-zinc-300">
              Tap <span className="font-semibold">Share</span> in Safari, then{' '}
              <span className="font-semibold">"Add to Home Screen"</span>.
            </p>
          )}
          {visible && deferredPrompt && (
            <button
              data-testid="pwa-install-button"
              onClick={handleInstall}
              className="mt-2 inline-flex items-center gap-2 rounded-full bg-purple-600 px-4 py-1.5 text-xs font-semibold text-white transition hover:bg-purple-500 active:scale-95"
            >
              <Download className="h-3.5 w-3.5" />
              Install Now
            </button>
          )}
        </div>
        <button
          data-testid="pwa-install-close"
          onClick={handleClose}
          aria-label="Dismiss install prompt"
          className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full text-zinc-400 transition hover:bg-zinc-800 hover:text-white"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}
